// What the Mac app's shell is told for its notifications and its count, on
// its `poulpe` message handler: data only, no credentials, transcript or
// executable commands. Its tabs, links and titles are the workspace's
// (workspace/, on `boardChrome`).
// Its separate `nativeStatus` report on `boardChrome` is display-only.
import { completedSessions, landingsAwaitingPerson } from '../sessions/attention'
import { useEffect, useMemo, useState } from 'react'
import type { Question } from '../api/types'
import { clockTime } from '../components/Time'
import { useExpiryClock, waits } from '../questions/waiting'
import { inMacShell, tellShell } from '../lib/shell'
import { useBoardState } from '../store/context'
import { needsYou, type BoardState } from '../store/store'

type Event = { id: string; path: string; title: string; body: string; pending: boolean }
export type MacSnapshot = { identity: string | null; count: number; events: Event[] }
type MacWindow = Window & { webkit?: { messageHandlers?: { poulpe?: { postMessage: (value: MacSnapshot & { document?: string }) => void } } } }

/**
 * What a question's notice says at `now` (Poulpe-609): whether it is urgent,
 * and whether its agent waits, until when, as Poulpe-404's card reads them.
 * Never the question's words, nor what the agent goes on with at its expiry.
 * Its id does not change at the expiry, so the expiry alerts no one again.
 */
function questionNotice(q: Question, now: number): Pick<Event, 'title' | 'body'> {
  const title = q.urgent ? 'Urgent · An agent needs you' : 'An agent needs you'
  if (!waits(q, now)) return { title, body: 'Open Poulpe to read and answer the question.' }
  const until = q.expiry ? ` until ${clockTime(q.expiry.at, now)}` : ''
  return { title, body: `The agent waits for your answer${until}. Open Poulpe to answer.` }
}

/** At most `n` characters, never splitting one, with an ellipsis when cut. */
function cut(text: string, n: number): string {
  const chars = [...text]
  return chars.length <= n ? text : `${chars.slice(0, n - 1).join('')}…`
}

/**
 * What names a notice's session (Poulpe-818): its issue's key first, its
 * title cut to 60 characters, and its agent's model. A local Mac notice
 * only: never a question's words, a commit or a repository.
 */
export function sessionName(state: BoardState, sessionId: string | null | undefined, issueId?: string | null): string {
  const session = sessionId ? state.sessions.get(sessionId) : undefined
  const issue = state.issues.get(session?.issue_id ?? issueId ?? '')
  const model = session ? session.settings?.current_model ?? session.agent.model : null
  return [issue?.key, session?.title && cut(session.title, 60), model && cut(model, 64)].filter(Boolean).join(' · ')
}

function named(name: string, body: string): string {
  return name ? `${name}. ${body}` : body
}

/**
 * Only what a person must act on (Poulpe-818): a question to them, a turn
 * ended in a session they started and drive themselves, which waits for
 * their next prompt, such a session's failure, a landing waiting for their
 * passkey approval and a stopped batch holding a proposal. A delegated
 * session's turn or failure is its lead's, and a landing asks nothing.
 */
export function macSnapshot(state: BoardState, now: number = Date.now()): MacSnapshot | null {
  if (state.status === 'loading' || state.status === 'error') return null
  const { me } = state
  if (state.status !== 'ready' || !me?.member) return { identity: null, count: 0, events: [] }
  const questions = needsYou(state.questions, me.member.id, state.pendingAnswers)
  const completed = completedSessions(state.sessions, state.questions, me.member.id, state.pendingAnswers)
  const events: Event[] = questions.map(q => {
    const { title, body } = questionNotice(q, now)
    return {
      id: `question:${q.id}:${q.version}:${q.delivery_revision}`,
      path: `/sessions/${encodeURIComponent(q.session)}`, title, body: named(sessionName(state, q.session), body), pending: true,
    }
  })
  for (const session of completed) events.push({
    id: `completed:${session.id}:${session.awaiting_person_since}`, path: `/sessions/${encodeURIComponent(session.id)}`,
    title: 'An agent has replied', body: named(sessionName(state, session.id), 'Open the session to continue or end it.'), pending: true,
  })
  for (const session of state.sessions.values()) {
    if (session.started_by === me.member.id && session.origin.kind === 'started' && session.ended?.outcome === 'failed') events.push({
      id: `failed:${session.id}`, path: `/sessions/${encodeURIComponent(session.id)}`,
      title: 'A session failed', body: named(sessionName(state, session.id), 'Open the session for details.'), pending: false,
    })
  }
  // Keep the same event population as the store. An arbitrary window would
  // starve new completions or replay old ones when pending questions change.
  // A completed turn is notified but not counted: it asks nothing of Needs
  // you. An approval or a stopped batch is counted, as Needs you counts it
  // (Poulpe-586), and notified, on the board's Ready to merge.
  const landings = landingsAwaitingPerson(state.landings, state.held, state.dismissed)
  for (const landing of landings) {
    const proposal = state.proposals.get(landing.proposal_id)
    const name = sessionName(state, proposal?.session_id, proposal?.issue_id)
    const approval = landing.waiting_for?.for === 'approval' ? landing.waiting_for : null
    events.push(approval
      ? { id: `approval:${landing.id}:${approval.digest}`, path: '/board', title: 'A landing needs your approval',
        body: named(name, 'Open Poulpe to approve it with your passkey.'), pending: true }
      : { id: `held:${landing.id}`, path: '/board', title: 'A stopped batch holds work',
        body: named(name, 'Open Poulpe to see what it needs.'), pending: true })
  }
  return { identity: `${me.workspace.id}/${me.member.id}`, count: questions.length + landings.length, events }
}

// An unchanged snapshot is posted again only at a store change after this
// long, doubling while it stays unchanged: native may have failed to add a
// notification and forgotten it, and only a new post lets it try again.
export const RESEND_MIN_MS = 2_000
export const RESEND_MAX_MS = 60_000

/** The first of these questions' expiries after `now`, when their notices change; Infinity when none comes. */
function nextExpiry(questions: ReadonlyMap<string, Question>, now: number): number {
  let next = Infinity
  for (const q of questions.values()) {
    const at = q.expiry ? Date.parse(q.expiry.at) : NaN
    if (at > now && at < next) next = at
  }
  return next
}

/** What macSnapshot reads, besides the clock: when none of it changed and no expiry passed, neither did the snapshot. */
function snapshotInputs(state: BoardState): readonly unknown[] {
  return [state.status, state.me, state.questions, state.pendingAnswers, state.sessions,
    state.issues, state.proposals, state.landings, state.held, state.dismissed]
}

/**
 * Posts a snapshot when it changed, and an unchanged one again only after a
 * bounded, growing delay. `post` says whether it reached the shell; one that
 * did not is tried at the next store change. Loading and errors post nothing.
 */
export function macNotificationPublisher(post: (snapshot: MacSnapshot) => boolean, now: () => number = Date.now) {
  let inputs: readonly unknown[] | null = null
  let expires = Infinity
  let current: { snapshot: MacSnapshot; text: string } | null = null
  let sent: string | null = null
  let sentAt = 0
  let resendAfter = RESEND_MIN_MS
  return (state: BoardState) => {
    const next = snapshotInputs(state)
    const at = now()
    if (!inputs || at >= expires || next.some((value, i) => value !== inputs![i])) {
      inputs = next
      expires = nextExpiry(state.questions, at)
      const snapshot = macSnapshot(state, at)
      current = snapshot && { snapshot, text: JSON.stringify(snapshot) }
    }
    if (!current) return
    const resend = current.text === sent
    if (resend && at - sentAt < resendAfter) return
    if (!post(current.snapshot)) return
    resendAfter = resend ? Math.min(resendAfter * 2, RESEND_MAX_MS) : RESEND_MIN_MS
    sent = current.text
    sentAt = at
  }
}

/** Tells the shell what it notifies of, when that changes, an expiry included. Drawn once per document. */
export function MacNotifications() {
  const state = useBoardState()
  const questions = useMemo(() => [...state.questions.values()], [state.questions])
  // Renders again when the next expiry passes, so its notice no longer says the agent waits.
  const now = useExpiryClock(questions)
  const [publish] = useState(() => {
    const nativeDocument = document.documentElement.dataset.sessionDocument
    return macNotificationPublisher(snapshot => {
      const bridge = (window as MacWindow).webkit?.messageHandlers?.poulpe
      bridge?.postMessage({ ...snapshot, document: nativeDocument })
      return !!bridge
    })
  })
  useEffect(() => { publish(state) }, [publish, state, now])
  return null
}

function statusDisplay(state: BoardState) {
  return { phase: state.status, offline: state.offline, stream: state.stream, waiting: state.waiting, refused: state.refused.length }
}

/** Captures this document once. Reports contain no identity, errors, changes or credentials. */
export function nativeStatusPublisher() {
  if (!inMacShell()) return null
  const document = globalThis.document.documentElement.dataset.sessionDocument
  if (!document) return null
  let revision = 0
  let previous: ReturnType<typeof statusDisplay> | null = null
  return (state: BoardState) => {
    const display = statusDisplay(state)
    if (previous && previous.phase === display.phase && previous.offline === display.offline
      && previous.stream === display.stream && previous.waiting === display.waiting && previous.refused === display.refused) return
    previous = display
    tellShell({ nativeStatus: { document, revision: ++revision, ...display } })
  }
}

/** One app-root publisher, including loading and signed-out states, never one per pane. */
export function MacStatus() {
  const state = useBoardState()
  const [publish] = useState(() => nativeStatusPublisher())
  useEffect(() => { publish?.(state) }, [publish, state])
  return null
}
